import { GraphQLRequestClient } from '@sitecore-jss/sitecore-jss-nextjs/graphql';
import graphqlClientFactory from 'lib/graphql-client-factory';
import { ArticleSearchDocument, ArticleSearchQuery } from './queries/ArticleSearch.graphql';
import {
  ArticleDataDocument,
  ArticleDataQuery,
  ArticleDataQueryVariables,
} from './queries/ArticleData.graphql';
import { ArticleCardData, ArticleData, RelatedArticle, ThemeData } from '../../model/Article';
import {
  ArticleSearchRelatedStoriesDocument,
  ArticleSearchRelatedStoriesQuery,
  ArticleSearchRelatedStoriesQueryVariables,
} from './queries/ArticleSearchRelatedStories.graphql';
import { ItemSearchOperator, ItemSearchPredicateInput } from 'model/Sitecore.graphql';
import {
  ArticleSearchRelatedByDatasourceDocument,
  ArticleSearchRelatedByDatasourceQuery,
  ArticleSearchRelatedByDatasourceQueryVariables,
} from './queries/ArticleSearchRelatedByDataSource.graphql';

import {
  isArticle,
  hasLanguage,
  sharedToSite,
  isNotLimited,
  hasAnyCapability,
  hasAnyCategory,
  hasAnyTag,
  hasAnyTheme,
  hasAllCapabilities,
  hasCategory,
  idIsNot,
  hasAnyId,
  hasThemeId,
} from './predicates/ArticlePredicates';
import {
  FindThemeDocument,
  FindThemeQuery,
  FindThemeQueryVariables,
} from './queries/FindTheme.graphql';
import {
  ArticleCountDocument,
  ArticleCountQuery,
  ArticleCountQueryVariables,
} from './queries/ArticleCount.graphql';
import {
  FindThemeByNameDocument,
  FindThemeByNameQuery,
  FindThemeByNameQueryVariables,
} from './queries/FindThemeByName.graphql';
import {
  GetPaginationCursorDocument,
  GetPaginationCursorQuery,
  GetPaginationCursorQueryVariables,
} from './queries/GetPaginationCursor.graphql';

class ArticleService {
  constructor(private readonly graphQLClient: GraphQLRequestClient) {}

  public async getArticleLayout(articleName: string, siteId: string, language: string) {
    const queryParams = {
      pageSize: 1,
      pageHash: '',
      articleName,
      language,
      siteId,
    };
    const result = await this.graphQLClient.request<ArticleSearchQuery>(
      ArticleSearchDocument,
      queryParams
    );
    const articleLayout = result.search?.results[0];
    return articleLayout;
  }

  private createPredicate(t: string): ItemSearchPredicateInput {
    return {
      name: 'articleTags',
      value: t,
      operator: ItemSearchOperator.Contains,
      AND: undefined,
      OR: undefined,
    };
  }

  public async getArticleData(id: string, language: string): Promise<ArticleData | undefined> {
    const queryParams: ArticleDataQueryVariables = { path: id, language };
    const response = await this.graphQLClient.request<ArticleDataQuery>(
      ArticleDataDocument,
      queryParams
    );
    const result = response?.item as ArticleData;
    return result;
  }

  public async getRelatedStories(
    language: string,
    siteId: string,
    article: ArticleData
  ): Promise<RelatedArticle[]> {
    if (article.articleTags?.targetItems.length === 0) return [];
    const queryParams: ArticleSearchRelatedStoriesQueryVariables = {
      pageSize: 5,
      language,
      siteId,
      currentArticleId: article.id,
      tags: article.articleTags?.targetIds?.map(this.createPredicate) ?? [],
    };
    const result = await this.graphQLClient.request<ArticleSearchRelatedStoriesQuery>(
      ArticleSearchRelatedStoriesDocument,
      queryParams
    );
    return (result.search?.results as RelatedArticle[]) ?? [];
  }

  private async getPaginationCursor(
    predicates: ItemSearchPredicateInput,
    itemsToSkip: number
  ): Promise<string | undefined> {
    if (itemsToSkip === 0) {
      return { cursor: undefined, total: 0 };
    }

    const queryParams: GetPaginationCursorQueryVariables = {
      itemsToSkip,
      predicates,
    };
    const response = await this.graphQLClient.request<GetPaginationCursorQuery>(
      GetPaginationCursorDocument,
      queryParams
    );

    return response.search?.pageInfo?.endCursor;
  }

  private async getArticleCards(
    predicates: ItemSearchPredicateInput,
    pageSize: number = 5,
    after?: string
  ): Promise<{ articles: ArticleCardData[]; total: number }> {
    const queryParams: ArticleSearchRelatedByDatasourceQueryVariables = {
      pageSize,
      predicates,
      after,
    };
    const response = await this.graphQLClient.request<ArticleSearchRelatedByDatasourceQuery>(
      ArticleSearchRelatedByDatasourceDocument,
      queryParams
    );
    const articles = (response.search?.results as ArticleCardData[]) ?? [];
    const total = response.search?.total ?? 0;
    return { articles, total };
  }

  public async getRelatedArticles(
    language: string,
    siteId: string,
    limitedDisplay: string,
    capabilities: string[],
    categories: string[] = [],
    tags: string[] = [],
    themes: string[] = []
  ): Promise<ArticleCardData[]> {
    const relatedArticlesPredicate = {
      AND: [
        isArticle,
        hasLanguage(language),
        sharedToSite(siteId),
        isNotLimited({ limitedDisplay, siteId }),
        capabilities?.length > 0 ? hasAnyCapability(capabilities) : undefined,
        categories?.length > 0 ? hasAnyCategory(categories) : undefined,
        tags?.length > 0 ? hasAnyTag(tags) : undefined,
        themes?.length > 0 ? hasAnyTheme(themes) : undefined,
      ].filter((e) => e),
    };
    const result = await this.getArticleCards(relatedArticlesPredicate);
    return result.articles;
  }

  public async getRelatedArticlesByPage(
    language: string,
    siteId: string,
    articleId: string,
    limitedDisplay: string,
    capabilities: string[],
    category: string
  ): Promise<ArticleCardData[]> {
    const relatedArticlesByPagePredicate = {
      AND: [
        isArticle,
        hasLanguage(language),
        sharedToSite(siteId),
        isNotLimited({ limitedDisplay, siteId }),
        idIsNot(articleId),
        category ? hasCategory(category) : undefined,
        capabilities?.length > 0 ? hasAllCapabilities(capabilities) : undefined,
      ].filter((e) => e),
    };
    const result = await this.getArticleCards(relatedArticlesByPagePredicate);
    return result.articles;
  }

  public async getFeaturedArticles(
    language: string,
    siteId: string,
    articleIds: string[]
  ): Promise<ArticleCardData[]> {
    const featuredArticlesPredicate = {
      AND: [
        isArticle,
        hasLanguage(language),
        sharedToSite(siteId),
        isNotLimited({ siteId }),
        hasAnyId(articleIds),
      ],
    };
    const result = await this.getArticleCards(featuredArticlesPredicate);
    return result.articles;
  }

  public async getFeaturedThemeArticles(
    language: string,
    siteId: string,
    themeId: string,
    pageSize: number = 5,
    pageNumber: number = 1
  ): Promise<{ articles: ArticleCardData[]; total: number }> {
    const featuredThemeArticlesPredicate = {
      AND: [
        isArticle,
        hasLanguage(language),
        sharedToSite(siteId),
        isNotLimited({ siteId }),
        hasThemeId(themeId),
      ],
    };

    // Calculate items to skip for the target page
    const itemsToSkip = (pageNumber - 1) * pageSize;

    if (pageNumber === 1) {
      // For first page, no cursor needed
      return this.getArticleCards(featuredThemeArticlesPredicate, pageSize);
    }

    const cursor = await this.getPaginationCursor(featuredThemeArticlesPredicate, itemsToSkip);

    return this.getArticleCards(featuredThemeArticlesPredicate, pageSize, cursor);
  }

  public async getTheme(themeId: string, language: string): Promise<ThemeData | undefined> {
    const queryParams: FindThemeQueryVariables = {
      themeId,
      language,
    };
    const result = await this.graphQLClient.request<FindThemeQuery>(FindThemeDocument, queryParams);
    return result.item as ThemeData;
  }

  public async getThemeByName(themeName: string, language: string): Promise<ThemeData | undefined> {
    const queryParams: FindThemeByNameQueryVariables = {
      themeName,
      language,
    };
    const response = await this.graphQLClient.request<FindThemeByNameQuery>(
      FindThemeByNameDocument,
      queryParams
    );
    return response.search?.results?.[0] as unknown as ThemeData;
  }

  public async getLatestArticles(language: string, siteId: string): Promise<ArticleCardData[]> {
    const latestArticlesPredicate = {
      AND: [isArticle, hasLanguage(language), sharedToSite(siteId), isNotLimited({ siteId })],
    };
    const result = await this.getArticleCards(latestArticlesPredicate);
    return result.articles;
  }

  public async getArticleCount(
    language: string,
    siteId: string,
    seriesId: string
  ): Promise<number> {
    const queryParams: ArticleCountQueryVariables = {
      language,
      siteId,
      seriesId,
    };

    const result = await this.graphQLClient.request<ArticleCountQuery>(
      ArticleCountDocument,
      queryParams
    );

    return result.search?.total || 0;
  }
}

export const articleService = new ArticleService(graphqlClientFactory());
